import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card'
import { Label } from '../../../ui/label'
import { FileUpload } from '../../file-upload'
import { Download, AlertCircle } from 'lucide-react'

interface OverviewWhitelistSectionProps {
  localData: {
    enableWhitelistAccess: boolean
    whitelistCsv: File | null
    whitelistAddressCount: number
  }
  errors: Record<string, string>
  downloadTemplate: () => void
  onWhitelistSelected: (file: File | null) => void
  whitelistInvalidCount: number
  whitelistDuplicateCount: number
  updateField: (field: string, value: any) => void
  requestChanges?: Record<string, string>
  variant?: 'card' | 'inline'
}

function WhitelistContent({
  localData,
  errors,
  downloadTemplate,
  onWhitelistSelected,
  whitelistInvalidCount,
  whitelistDuplicateCount,
  updateField,
  requestChanges,
  variant = 'card',
}: OverviewWhitelistSectionProps) {
  return (
    <>
      <div className={variant === 'inline' ? '-mt-3' : 'space-y-2'}>
        <Label
          className={`text-sm font-medium cursor-pointer flex items-center gap-2 ${
            variant === 'inline' ? '-mt-1' : ''
          }`}
        >
          <input
            type="checkbox"
            checked={localData.enableWhitelistAccess}
            onChange={(e) =>
              updateField('enableWhitelistAccess', e.target.checked)
            }
            className="w-4 h-4 rounded border-neutral-300 text-brand-primary focus:ring-brand-primary accent-brand-primary"
          />
          Enable Whitelist Access
        </Label>
        <p className="text-xs text-neutral-500 ml-6">
          Upload a list of wallet addresses that should receive guaranteed
          allocations.
        </p>
      </div>
      {localData.enableWhitelistAccess && (
        <div className="space-y-2">
          <FileUpload
            mode="document"
            accept="text/csv,.csv"
            buttonLabel="Upload CSV"
            helperText={
              <span>
                CSV only • Max 5 MB • Use the{' '}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    downloadTemplate()
                  }}
                  className="text-brand-primary hover:text-brand-hover underline inline-flex items-center gap-1"
                >
                  Whitelisted Address Template
                  <Download className="w-3 h-3" />
                </button>
              </span>
            }
            maxSizeMB={5}
            errorStrings={{
              fileCount: 'Please select only one CSV file.',
              fileType: 'Invalid CSV header. Use the provided template.',
              fileSize: 'File size must be 5 MB or less.',
              unreadable:
                'Unable to read the CSV file. Please check the format.',
              empty: 'The CSV file appears to be empty.',
              generic: 'An error occurred while processing the file.',
            }}
            ariaLabel="Upload whitelist CSV"
            onFileSelect={onWhitelistSelected}
            currentFile={localData.whitelistCsv}
            error={errors.whitelistCsv}
          />
          <div className="flex flex-col gap-1">
            {requestChanges?.whitelistCsv && (
              <p className="text-status-error text-xs inline-flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {requestChanges.whitelistCsv}
              </p>
            )}
          </div>
          {localData.whitelistCsv && !errors.whitelistCsv && (
            <p className="text-xs text-neutral-500">
              {localData.whitelistAddressCount || 0} addresses
              {whitelistInvalidCount > 0 && (
                <span className="text-status-error">
                  {' '}
                  • {whitelistInvalidCount} invalid address
                  {whitelistInvalidCount > 1 ? 'es' : ''} removed
                </span>
              )}
              {whitelistDuplicateCount > 0 && (
                <span className="text-status-error">
                  {' '}
                  • {whitelistDuplicateCount} duplicate
                  {whitelistDuplicateCount > 1 ? 's' : ''} removed
                </span>
              )}
            </p>
          )}
        </div>
      )}
    </>
  )
}

export function OverviewWhitelistSection({
  localData,
  errors,
  downloadTemplate,
  onWhitelistSelected,
  whitelistInvalidCount,
  whitelistDuplicateCount,
  updateField,
  requestChanges,
  variant = 'card',
}: OverviewWhitelistSectionProps) {
  if (variant === 'inline') {
    return (
      <WhitelistContent
        {...{
          localData,
          errors,
          downloadTemplate,
          onWhitelistSelected,
          whitelistInvalidCount,
          whitelistDuplicateCount,
          updateField,
          requestChanges,
          variant,
        }}
      />
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Whitelist Access</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <WhitelistContent
          {...{
            localData,
            errors,
            downloadTemplate,
            onWhitelistSelected,
            whitelistInvalidCount,
            whitelistDuplicateCount,
            updateField,
            requestChanges,
            variant,
          }}
        />
      </CardContent>
    </Card>
  )
}
